import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Globe, ArrowRight, Loader2, Download, Building, User, FileText, Settings } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContactForm } from "@/hooks/useContactForm";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const standardOptions = ["ISO/IEC 27001:2022", "ISO 9001:2015", "ISO 22301:2019", "ISO 27701:2019", "Compliance Assessment", "Cybersecurity Assurance", "Other"];
const certTypes = ["Initial Certification", "Re-Certification", "Transfer of Certification", "Scope Extension"];
const implStatuses = ["Fully Implemented", "Partially Implemented", "Planning Stage", "Not Yet Started"];

const inputCls = "rounded-none bg-[#F7F9FC] border-[#0B2545]/15 h-11 text-sm";

function SectionHeader({ icon: Icon, number, title }) {
  return (
    <div className="flex items-center gap-3 mb-6 pb-3 border-b-2 border-[#13A8BD]">
      <div className="w-8 h-8 bg-[#0B2545] flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-black text-white" style={{ fontFamily: "Chivo, sans-serif" }}>{number}</span>
      </div>
      <Icon size={18} className="text-[#13A8BD]" />
      <h3 className="text-sm font-bold text-[#0B2545] uppercase tracking-wider" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h3>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0B2545] mb-2 block">
        {label} {required && <span className="text-[#13A8BD]">*</span>}
      </Label>
      {children}
    </div>
  );
}

function SuccessMessage({ submissionId, c, onReset }) {
  return (
    <div data-testid="contact-success" className="border border-[#13A8BD]/30 bg-[#13A8BD]/5 p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#13A8BD] flex items-center justify-center">
        <FileText size={24} className="text-[#13A8BD]" />
      </div>
      <h3 className="text-2xl font-bold text-[#0B2545] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>{c.success_title || "Application Received"}</h3>
      <p className="text-sm text-slate-600 mb-6">{c.success_msg || "Thank you for your application. Our certification team will review your submission and respond within 1-2 business days with a detailed quotation and audit plan."}</p>
      {submissionId && (
        <a href={`${API}/contact/${submissionId}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="download-application-pdf"
          className="inline-flex items-center gap-2 bg-[#0B2545] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#13A8BD] transition-colors mb-4">
          <Download size={16} /> {c.download_pdf || "Download Application PDF"}
        </a>
      )}
      <br />
      <button onClick={onReset} data-testid="contact-submit-another" className="mt-2 text-sm font-semibold text-[#13A8BD] uppercase tracking-wider hover:text-[#0e8a9c] transition-colors">
        {c.submit_another || "Submit Another Application"}
      </button>
    </div>
  );
}

function StepOrgInfo({ form, updateField, updateDetail, c }) {
  return (
    <div data-testid="form-step-1">
      <SectionHeader icon={Building} number="01" title={c.section1 || "Organization Information"} />
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label={c.org_name || "Organization Name"} required>
            <Input data-testid="contact-org-input" value={form.organization_name} onChange={(e) => updateField("organization_name", e.target.value)} className={inputCls} />
          </FormField>
          <FormField label={c.person || "Contact Person"} required>
            <Input data-testid="contact-person-input" value={form.contact_person} onChange={(e) => updateField("contact_person", e.target.value)} className={inputCls} />
          </FormField>
        </div>
        <FormField label={c.job_title || "Job Title / Position"}>
          <Input data-testid="contact-jobtitle-input" value={form.details.job_title} onChange={(e) => updateDetail("job_title", e.target.value)} className={inputCls} />
        </FormField>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label={c.email || "Email Address"} required>
            <Input type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => updateField("email", e.target.value)} className={inputCls} />
          </FormField>
          <FormField label={c.phone || "Phone"}>
            <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} className={inputCls} />
          </FormField>
        </div>
        <FormField label={c.address || "Legal Address"}>
          <Input data-testid="contact-address-input" value={form.details.address} onChange={(e) => updateDetail("address", e.target.value)} className={inputCls} />
        </FormField>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <FormField label={c.city || "City"}>
            <Input data-testid="contact-city-input" value={form.details.city} onChange={(e) => updateDetail("city", e.target.value)} className={inputCls} />
          </FormField>
          <FormField label={c.country || "Country"}>
            <Input data-testid="contact-country-input" value={form.details.country} onChange={(e) => updateDetail("country", e.target.value)} className={inputCls} />
          </FormField>
          <FormField label={c.website_label || "Website"}>
            <Input data-testid="contact-website-input" value={form.details.website} onChange={(e) => updateDetail("website", e.target.value)} className={inputCls} />
          </FormField>
        </div>
      </div>
    </div>
  );
}

function StepCertDetails({ form, updateField, updateDetail, c }) {
  return (
    <div data-testid="form-step-2">
      <SectionHeader icon={FileText} number="02" title={c.section2 || "Certification Details"} />
      <div className="space-y-5">
        <FormField label={c.standard || "Standard Requested"} required>
          <Select value={form.standard_requested} onValueChange={(val) => updateField("standard_requested", val)}>
            <SelectTrigger data-testid="contact-standard-select" className="rounded-none bg-[#F7F9FC] border-[#0B2545]/15 h-11"><SelectValue placeholder="Select a standard" /></SelectTrigger>
            <SelectContent>
              {standardOptions.map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={c.cert_type || "Certification Type"}>
          <Select value={form.details.cert_type} onValueChange={(val) => updateDetail("cert_type", val)}>
            <SelectTrigger data-testid="contact-certtype-select" className="rounded-none bg-[#F7F9FC] border-[#0B2545]/15 h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {certTypes.map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={c.scope || "Scope of Certification"}>
          <Textarea data-testid="contact-scope-input" value={form.details.scope} onChange={(e) => updateDetail("scope", e.target.value)} rows={3} className="rounded-none bg-[#F7F9FC] border-[#0B2545]/15 resize-none text-sm" placeholder="Describe the activities, products, or services to be covered by the certification" />
        </FormField>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label={c.num_employees || "Number of Employees"}>
            <Input type="number" data-testid="contact-employees-input" value={form.details.num_employees} onChange={(e) => updateDetail("num_employees", e.target.value)} className={inputCls} placeholder="e.g. 250" />
          </FormField>
          <FormField label={c.num_sites || "Number of Sites / Locations"}>
            <Input type="number" data-testid="contact-sites-input" value={form.details.num_sites} onChange={(e) => updateDetail("num_sites", e.target.value)} className={inputCls} placeholder="e.g. 3" />
          </FormField>
        </div>
      </div>
    </div>
  );
}

function StepManagementSystem({ form, updateDetail, c }) {
  return (
    <div data-testid="form-step-3">
      <SectionHeader icon={Settings} number="03" title={c.section3 || "Management System Information"} />
      <div className="space-y-5">
        <FormField label={c.existing_certs || "Existing Certifications"}>
          <Input data-testid="contact-existingcerts-input" value={form.details.existing_certs} onChange={(e) => updateDetail("existing_certs", e.target.value)} className={inputCls} placeholder="e.g. ISO 9001:2015, ISO 14001:2015" />
        </FormField>
        <FormField label={c.impl_status || "Implementation Status"}>
          <Select value={form.details.implementation_status} onValueChange={(val) => updateDetail("implementation_status", val)}>
            <SelectTrigger data-testid="contact-implstatus-select" className="rounded-none bg-[#F7F9FC] border-[#0B2545]/15 h-11"><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              {implStatuses.map((opt) => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}
            </SelectContent>
          </Select>
        </FormField>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label={c.consultant || "Consultant Used"}>
            <Select value={form.details.consultant_used} onValueChange={(val) => updateDetail("consultant_used", val)}>
              <SelectTrigger data-testid="contact-consultant-select" className="rounded-none bg-[#F7F9FC] border-[#0B2545]/15 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Planning to">Planning to</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label={c.preferred_date || "Preferred Audit Date"}>
            <Input type="date" data-testid="contact-auditdate-input" value={form.details.preferred_audit_date} onChange={(e) => updateDetail("preferred_audit_date", e.target.value)} className={inputCls} />
          </FormField>
        </div>
      </div>
    </div>
  );
}

function StepAdditional({ form, updateField, c }) {
  return (
    <div data-testid="form-step-4">
      <SectionHeader icon={User} number="04" title={c.section4 || "Additional Information"} />
      <div className="space-y-5">
        <FormField label={c.message || "Additional Notes / Message"}>
          <Textarea data-testid="contact-message-input" value={form.message} onChange={(e) => updateField("message", e.target.value)} rows={6} className="rounded-none bg-[#F7F9FC] border-[#0B2545]/15 resize-none text-sm" placeholder="Any additional information, questions, or specific requirements" />
        </FormField>
        <div className="border border-[#0B2545]/10 bg-[#F7F9FC] p-6" data-testid="form-summary">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">Application Summary</h4>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
            <div><span className="text-slate-400">Organization:</span> <span className="font-medium text-[#0B2545]">{form.organization_name || "—"}</span></div>
            <div><span className="text-slate-400">Contact:</span> <span className="font-medium text-[#0B2545]">{form.contact_person || "—"}</span></div>
            <div><span className="text-slate-400">Email:</span> <span className="font-medium text-[#0B2545]">{form.email || "—"}</span></div>
            <div><span className="text-slate-400">Standard:</span> <span className="font-medium text-[#13A8BD]">{form.standard_requested || "—"}</span></div>
            <div><span className="text-slate-400">Employees:</span> <span className="font-medium text-[#0B2545]">{form.details.num_employees || "—"}</span></div>
            <div><span className="text-slate-400">Sites:</span> <span className="font-medium text-[#0B2545]">{form.details.num_sites || "—"}</span></div>
            <div><span className="text-slate-400">Type:</span> <span className="font-medium text-[#0B2545]">{form.details.cert_type || "—"}</span></div>
            <div><span className="text-slate-400">Status:</span> <span className="font-medium text-[#0B2545]">{form.details.implementation_status || "—"}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactSidebar({ c }) {
  return (
    <div className="lg:col-span-1">
      <div className="border border-[#0B2545]/10 p-8 sticky top-28 space-y-8">
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-5">{c.contact_info || "Contact Information"}</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail size={16} className="text-[#13A8BD] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">{c.certification_label || "Certification"}</p>
                <a href="mailto:certification@gacaint.com" data-testid="contact-email-cert" className="text-sm font-medium text-[#0B2545] hover:text-[#13A8BD] transition-colors">certification@gacaint.com</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={16} className="text-[#13A8BD] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">{c.general_label || "General Inquiries"}</p>
                <a href="mailto:info@gacaint.com" data-testid="contact-email-info" className="text-sm font-medium text-[#0B2545] hover:text-[#13A8BD] transition-colors">info@gacaint.com</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Globe size={16} className="text-[#13A8BD] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">{c.website_label || "Website"}</p>
                <a href="https://gacaint.com" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#0B2545] hover:text-[#13A8BD] transition-colors">gacaint.com</a>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-[#0B2545]/10 pt-6">
          <p className="text-[10px] text-slate-400 leading-relaxed">{c.disclaimer || "All certification services are delivered through our internationally accredited partner bodies. Certification decisions are made independently and impartially."}</p>
        </div>
      </div>
    </div>
  );
}

export default function ContactPage() {
  const { step, setStep, loading, submitted, submissionId, form, updateField, updateDetail, canProceed, handleSubmit, resetForm } = useContactForm();
  const { t } = useLang();
  const c = t?.contact || {};

  const renderStep = () => {
    switch (step) {
      case 1: return <StepOrgInfo form={form} updateField={updateField} updateDetail={updateDetail} c={c} />;
      case 2: return <StepCertDetails form={form} updateField={updateField} updateDetail={updateDetail} c={c} />;
      case 3: return <StepManagementSystem form={form} updateDetail={updateDetail} c={c} />;
      case 4: return <StepAdditional form={form} updateField={updateField} c={c} />;
      default: return null;
    }
  };

  return (
    <div className="page-transition pt-20" data-testid="contact-page">
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#13A8BD] mb-4">{t?.nav?.apply || "Apply"}</p>
          <h1 data-testid="contact-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {c.title || "Apply for Certification"}
          </h1>
          <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            {c.subtitle || "Complete the application form below. Our team will review your scope, assign an audit team, and provide a detailed quotation."}
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            <div className="lg:col-span-2">
              {submitted ? (
                <SuccessMessage submissionId={submissionId} c={c} onReset={resetForm} />
              ) : (
                <div>
                  <div className="flex items-center gap-0 mb-10" data-testid="form-progress">
                    {[1, 2, 3, 4].map((s) => (
                      <div key={s} className="flex items-center flex-1 last:flex-none">
                        <button onClick={() => s < step && setStep(s)}
                          className={`w-9 h-9 flex items-center justify-center text-xs font-black transition-colors ${
                            s === step ? 'bg-[#13A8BD] text-white' : s < step ? 'bg-[#0B2545] text-white cursor-pointer' : 'bg-[#F7F9FC] text-slate-400 border border-[#0B2545]/10'
                          }`} style={{ fontFamily: "Chivo, sans-serif" }}>{s}</button>
                        {s < 4 && <div className={`flex-1 h-[2px] ${s < step ? 'bg-[#0B2545]' : 'bg-[#0B2545]/10'}`} />}
                      </div>
                    ))}
                  </div>

                  {renderStep()}

                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#0B2545]/10">
                    {step > 1 ? (
                      <button onClick={() => setStep(step - 1)} data-testid="form-prev-button" className="text-sm font-semibold text-[#0B2545] uppercase tracking-wider hover:text-[#13A8BD] transition-colors">Back</button>
                    ) : <div />}
                    {step < 4 ? (
                      <button onClick={() => canProceed() ? setStep(step + 1) : toast.error("Please fill required fields.")} data-testid="form-next-button"
                        className="inline-flex items-center gap-2 bg-[#0B2545] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#13A8BD] transition-colors">
                        Continue <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button onClick={handleSubmit} disabled={loading} data-testid="contact-submit-button"
                        className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors disabled:opacity-50">
                        {loading ? (<><Loader2 size={16} className="animate-spin" /> {c.submitting || "Submitting..."}</>) : (<>{c.submit || "Submit Application"} <ArrowRight size={16} /></>)}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <ContactSidebar c={c} />
          </div>
        </div>
      </section>
    </div>
  );
}
